import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Calculator, FilePen } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PERCORSI, vociDi } from "@/lib/accessi";
import { navIcons } from "@/lib/icons";
import { oggi } from "@/lib/crm";
import { NavCounter } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

/* Il menu blu (sistema del 29-10). Le voci e chi le vede restano quelle di `lib/accessi.js`: il menu cambia
   aspetto, non permessi. I gruppi seguono il ruolo (pacchetto, `navigation.ts`): il venditore ha «Dopo la
   firma» per i Contratti e «Aiuto» per le Guide, perché «Amministrazione» e «Sistema» non sono il suo lavoro. */
export const GRUPPI = ["Ogni giorno", "Amministrazione", "Sistema"];
const GRUPPI_DEL_VENDITORE = ["Ogni giorno", "Dopo la firma", "Aiuto"];
export const gruppiDi = (ruolo) => (ruolo === "venditore" ? GRUPPI_DEL_VENDITORE : GRUPPI);

export const menuItems = [
  { id: "cruscotto", label: "Dashboard", icon: navIcons.dashboard, path: PERCORSI.cruscotto, gruppo: 0 },
  { id: "oggi", label: "Oggi", icon: navIcons.oggi, path: PERCORSI.oggi, gruppo: 0 },
  { id: "clienti", label: "Clienti", icon: navIcons.clienti, path: PERCORSI.clienti, gruppo: 0 },
  { id: "calendario", label: "Calendario", icon: navIcons.calendario, path: PERCORSI.calendario, gruppo: 0 },
  {
    id: "preventivi",
    label: "Preventivi",
    icon: navIcons.preventivi,
    path: PERCORSI.preventivi,
    gruppo: 0,
    children: [
      { id: "calcolatore", label: "Calcolatore", icon: Calculator, path: PERCORSI.calcolatore },
      { id: "preventivatore", label: "Preventivatore", icon: FilePen, path: PERCORSI.preventivatore },
    ],
  },
  { id: "listini", label: "Listini", icon: navIcons.listini, path: PERCORSI.listini, gruppo: 0 },
  { id: "assistenza", label: "Assistenza", icon: navIcons.assistenza, path: PERCORSI.assistenza, gruppo: 0 },
  { id: "messaggi", label: "Messaggi", icon: navIcons.messaggi, path: PERCORSI.messaggi, gruppo: 0 },
  { id: "contratti", label: "Contratti", icon: navIcons.contratti, path: PERCORSI.contratti, gruppo: 1 },
  { id: "fatture-incassi", label: "Fatture e incassi", breve: "Fatture", icon: navIcons.fatture, path: PERCORSI["fatture-incassi"], gruppo: 1 },
  { id: "impostazioni", label: "Impostazioni", icon: navIcons.impostazioni, path: PERCORSI.impostazioni, gruppo: 2 },
  { id: "guide", label: "Guide", icon: navIcons.guide, path: PERCORSI.guide, gruppo: 2 },
];

/** Il menu di una persona: le voci del ruolo meno le sezioni spente, gruppi compresi. */
export function menuPerRuolo(ruolo, spente) {
  const mie = new Set(vociDi(ruolo, spente));
  return menuItems
    .filter((item) => mie.has(item.id))
    .map((item) => (item.children ? { ...item, children: item.children.filter((c) => mie.has(c.id)) } : item));
}

/** Ogni voce, gruppi aperti: serve alla barra in basso per trovare etichetta e icona. */
export const tutteLeVoci = menuItems.flatMap((item) => [item, ...(item.children || [])]);

/* I numeri del menu (piano CRM: al massimo questi). Clienti: visite in showroom senza esito. Calendario: gli
   appuntamenti di oggi non annullati. Assistenza: le aperte, e se ce n'è di scadute dice quelle, in rosso.
   Messaggi: le conversazioni passate a una persona che aspettano. Fatture e incassi: le rate in ritardo.
   Si rileggono a ogni cambio di pagina. Calendario e rate si chiedono solo a chi ha la voce: agli altri il
   server direbbe di no, e un no nella console non è un conteggio. */
const DOPO_IL_NUMERO = { clienti: " visite senza esito", assistenza: " ancora da chiudere", messaggi: " da richiamare" };
const SCRITTO = {
  calendario: (n) => `${n} oggi`,
  "fatture-incassi": (n) => `${n} ${n === 1 ? "rata" : "rate"}`,
};
const SPIEGATO = { calendario: " appuntamenti", "fatture-incassi": " in ritardo" };

export function useConteggiMenu() {
  const { pathname } = useLocation();
  const { profile } = useAuth() || {};
  const voci = vociDi(profile?.ruolo, profile?.sezioni_spente);
  const conCalendario = voci.includes("calendario");
  const conRate = voci.includes("fatture-incassi");
  const [n, setN] = useState({ clienti: 0, assistenza: 0, scadute: 0, messaggi: 0, calendario: 0, "fatture-incassi": 0 });
  useEffect(() => {
    let vivo = true;
    const g = oggi();
    Promise.allSettled([
      api.get("/crm/conteggi"),
      api.get("/messaggi/conteggi"),
      api.get("/assistenze/conteggi"),
      conCalendario ? api.get("/calendario", { params: { da: g, a: g } }) : Promise.resolve(null),
      conRate ? api.get("/incassi") : Promise.resolve(null),
    ]).then(([c, m, a, cal, inc]) => {
      if (!vivo) return;
      const ok = (x) => x.status === "fulfilled" && x.value;
      setN({
        clienti: ok(c) ? c.value.data?.da_confermare || 0 : 0,
        messaggi: ok(m) ? m.value.data?.escalation_aperte || 0 : 0,
        assistenza: ok(a) ? a.value.data?.aperte || 0 : 0,
        scadute: ok(a) ? a.value.data?.scadute || 0 : 0,
        calendario: ok(cal) ? (cal.value.data?.appuntamenti || []).filter((x) => x.stato !== "annullato").length : 0,
        "fatture-incassi": ok(inc) ? inc.value.data?.riepilogo?.rate_in_ritardo || 0 : 0,
      });
    });
    return () => {
      vivo = false;
    };
  }, [pathname, conCalendario, conRate]);
  return n;
}

function Contatore({ id, n }) {
  if (id === "assistenza" && n.scadute > 0)
    return (
      <NavCounter tone="danger" data-testid="nav-assistenza-conteggio">
        {n.scadute} {n.scadute === 1 ? "scaduta" : "scadute"}
        <span className="sr-only"> su {n.assistenza} ancora da chiudere</span>
      </NavCounter>
    );
  if (!n[id]) return null;
  return (
    <NavCounter data-testid={`nav-${id}-conteggio`}>
      {SCRITTO[id] ? SCRITTO[id](n[id]) : n[id]}
      <span className="sr-only">{DOPO_IL_NUMERO[id] || SPIEGATO[id]}</span>
    </NavCounter>
  );
}

/* Voce: 40 px sul computer, 44 nel menu del telefono. Aperta = fondo chiaro sul blu e testo in grassetto. */
function Voce({ item, sotto = false, onNavigate, conteggi }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.path === PERCORSI.preventivi}
      onClick={onNavigate}
      data-testid={`nav-${item.id}`}
      className={({ isActive }) =>
        cn(
          "flex h-11 items-center gap-3 rounded-sm px-3 transition-colors duration-150 ease-enter lg:h-10",
          sotto && "pl-11 text-small lg:h-9",
          isActive ? "bg-nav-active font-semibold text-white" : "font-medium text-white/85 hover:bg-white/10 hover:text-white",
        )
      }
    >
      <Icon className={sotto ? "size-4" : "size-5"} strokeWidth={1.75} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {conteggi ? <Contatore id={item.id} n={conteggi} /> : null}
    </NavLink>
  );
}

export default function SidebarNav({ onNavigate }) {
  const conteggi = useConteggiMenu();
  const { profile } = useAuth() || {};
  const menu = menuPerRuolo(profile?.ruolo, profile?.sezioni_spente);
  return (
    <nav className="flex flex-col" aria-label="Menu principale">
      {gruppiDi(profile?.ruolo).map((titolo, g) => {
        const voci = menu.filter((i) => i.gruppo === g);
        if (!voci.length) return null;
        return (
          <div key={titolo} className="mt-4 first:mt-0">
            <p className="mb-1.5 px-3 text-caption text-white/70">{titolo}</p>
            <div className="flex flex-col gap-0.5">
              {voci.map((item) => (
                <div key={item.id} className="flex flex-col gap-0.5">
                  <Voce item={item} onNavigate={onNavigate} conteggi={conteggi} />
                  {(item.children || []).map((c) => (
                    <Voce key={c.id} item={c} sotto onNavigate={onNavigate} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </nav>
  );
}
